import Editor, { type OnMount } from '@monaco-editor/react';
import { useRef } from 'react';
import type { FileType } from '../types/theme';

function languageFor(type: FileType): string {
  // Language files are JSON; templates are HTML/Liquid (Monaco highlights the HTML).
  return type === 'language' ? 'json' : 'html';
}

const EDITOR_OPTIONS = {
  automaticLayout: true,
  autoClosingBrackets: 'always',
  autoClosingQuotes: 'always',
  formatOnType: true,
  formatOnPaste: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
} as const;

export function CodeEditor({
  value,
  fileType,
  onChange,
  onSave,
}: {
  value: string;
  fileType: FileType;
  onChange: (value: string) => void;
  onSave: () => void;
}) {
  // Keep the latest onSave so the Monaco command isn't a stale closure.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const handleMount: OnMount = (editor, monaco) => {
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => onSaveRef.current());
  };

  return (
    <Editor
      theme="vs-light"
      language={languageFor(fileType)}
      value={value}
      onChange={(next) => onChange(next ?? '')}
      onMount={handleMount}
      options={EDITOR_OPTIONS}
    />
  );
}
